import { Alert } from '../../api/utils';
import {
  changeDisplayType,
  type DocumentsPendingState,
  type DocumentsStoreState,
  type Filters,
  type FilterStoreState,
  handleFetchLatestDocuments,
  type OrganizationsStoreState,
  type Pagination,
  resetLatestDocuments,
} from '../../store/slices';
import { DocumentCategory } from './DocumentCategory';
import { EmptyDocumentCategory } from './DocumentCategory/EmptyDocumentCategory';
import { DocumentsPeriodSelector } from './DocumentsPeriodSelector/DocumentsPeriodSelector';
import { Dropdown } from 'components/Dropdown';
import { Button } from 'components/InteractiveUIControls/Button/Button';
import { Spinner } from 'components/Spinner';
import { ModalActivationContext } from 'contexts';
import { type SelectOption } from 'hooks';
import {
  type SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import { isTablet } from 'services/mobile';
import { type AppDispatch } from 'store';
import { type DocumentDTO } from 'types';
import { COLORS } from 'types/Colors';

const categoriesOptions: SelectOption[] = [
  {
    display: 'by type',
    id: 'type',
  },
  {
    display: 'by period',
    id: 'period',
  },
];

export const DocumentCategories = () => {
  const dispatch: AppDispatch = useDispatch();
  const [showSelection, setShowSelection] = useState(false);
  const { toggleDownloadsModal } = useContext(ModalActivationContext);
  const componentRef = useRef<HTMLDivElement>(null);
  const {
    categoriesError,
    categoriesPending,
    displayType,
    displayTypePending,
    newCategory,
    otherCategories,
  } = useSelector<{ documents: DocumentsStoreState, }>(
    (state) => state.documents,
  ) as DocumentsStoreState;

  const {
    displayedOrganizationWithPermissions } = useSelector<{
    organizations: OrganizationsStoreState,
  }>((state) => state.organizations) as OrganizationsStoreState;

  const latestDocuments = useSelector<{ documents: DocumentsStoreState, }>(
    (state) => state.documents.latestDocuments,
  ) as DocumentDTO[] | null;

  const activeFilters = useSelector<{
    filters: FilterStoreState,
  }>((state) => state.filters.activeFilters) as Filters | null;

  const documentPendingState = useSelector<{ documents: DocumentsStoreState, }>(
    (state) => state.documents.documentsPending,
  ) as DocumentsPendingState;

  const organizationsPending = useSelector<{
    organizationsPending: OrganizationsStoreState,
  }>((state) => state.organizationsPending) as OrganizationsStoreState;

  const pagination = useSelector<{ documents: DocumentsStoreState, }>(
    (state) => state.documents.pagination,
  ) as Pagination;

  const setNewDisplayType = async (value: SelectOption) => {
    dispatch(changeDisplayType(value));
  };

  const handleActionsButtonClick = (
    open: SetStateAction<boolean>,
    event: React.MouseEvent<HTMLElement, MouseEvent> | undefined,
  ) => {
    event?.stopPropagation();
    if (open === showSelection) {
      return;
    }

    setShowSelection(open);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleClickOutside = (event: MouseEvent) => {
    if (
      showSelection &&
       componentRef.current &&
       !componentRef.current.contains(event.target as Node)
    ) {
      setShowSelection(false);
    }
  };

  useEffect(() => {
    if (
      !latestDocuments &&
      !documentPendingState.pending &&
      !documentPendingState.error &&
      activeFilters
    ) {
      handleFetchLatestDocuments({
        dispatch,
        displayType,
        filters: activeFilters,
        firstFetch: true,
        pagination,
      });
    }
  });

  useEffect(() => {
    dispatch(resetLatestDocuments());
  }, [
    activeFilters,
    dispatch,
  ]);

  useEffect(() => {
    if (categoriesError) {
      Alert(categoriesError);
    }
  }, [
    categoriesError,
  ]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
    };
  }, [
    handleClickOutside,
  ]);

  const renderCategories = () =>
    <div className='flex size-full max-w-[90rem] flex-col gap-[1rem] rounded-medium ms:gap-0 ms:bg-white-999 ms:p-[1.25rem] ms:shadow-card-x3' data-test='all-documents-container'>
      {otherCategories && otherCategories?.length > 0 ? <div className='flex items-center justify-between'>
        <div className='flex items-center gap-[0.125rem]'>
          <h3 className='m-0 text-title-4 font-semibold'>
            View
          </h3>
          <Dropdown
            className={`relative ${displayType.id === 'period' ? 'max-w-28' : 'max-w-24'} ${displayTypePending || categoriesPending && 'opacity-50'}`}
            disabled={displayTypePending || categoriesPending}
            displayColor={COLORS['$main-700']}
            displayFontWeight='600'
            initiallySelected={categoriesOptions
              .find((opt) => opt.id === displayType.id) as SelectOption}
            optionContainerStyle='!px-[0.375rem]'
            optionList={categoriesOptions}
            optionTextStyle='!text-title-4 font-normal'
            optionsClassName='!absolute !top-0  !mt-0 !w-max !left-0'
            placeholder={''}
            selectedOptionContainerStyle='!text-main-700 flex items-center gap-[0.5rem]'
            setValue={(value) => setNewDisplayType(value)}
            type='transparent'
            value={categoriesOptions
              .find((opt) => opt.id === displayType.id) as SelectOption}
            withAltDesign
            withTickIcon
          />
        </div>
        <div className='flex gap-[0.375rem]'>
          <div className='relative' ref={componentRef}>
            <Button
              className='!h-8 !w-8 !border-black-200 hover:!bg-white-999 ms:!w-full'
              icon={isTablet() ? 'only' : 'left'}
              iconid='action-1f'
              onClick={(event) => handleActionsButtonClick(!showSelection, event)}
              size='medium'
              squared
              type='outline-grey'
            >
              {isTablet() ? null : <p className='m-0 !font-normal'>Actions</p>}
            </Button>
            {showSelection ?
              <div className='absolute top-9 z-10 flex w-max flex-col items-start gap-[0.375rem] rounded-main bg-white-999 p-[0.375rem] shadow-card-x3'>
                <Button
                  className='!border-transparent'
                  disabled={!displayedOrganizationWithPermissions}
                  icon={'left'}
                  iconid='download-cloud'
                  onClick={() => toggleDownloadsModal(true, null)}
                  size='medium'
                  squared
                  text='Bulk download'
                  tooltipWrapperClass='!justify-start'
                  type='outline-grey'
                />
              </div>
              : null}
          </div>
          <DocumentsPeriodSelector />
        </div>

      </div> : null}
      {categoriesPending || organizationsPending?.organizationsPending ? <div className='flex min-h-52 items-center justify-center'><Spinner /></div> :

        // eslint-disable-next-line @typescript-eslint/no-extra-parens
        (newCategory || otherCategories && otherCategories?.length > 0 ?
          otherCategories?.map((category) =>
            <DocumentCategory
              key={category.categoryId}
              {...category}
            />)
          : <EmptyDocumentCategory />)

      }
    </div>;

  return (
    <>{renderCategories()}</>
  );
};
